
/**
 * SolutionCard.tsx
 *
 * SolutionCard component for the Marketplace UI.
 * Displays a solution card with logo, title, author, description, tags, and entitlement status.
 *
 * Features:
 * - Shows solution logo, title, author, and description
 * - Displays tags and entitlement status
 * - Responsive, accessible, and navigable as a link
 *
 * @module SolutionCard
 */

import React from 'react';
import type { Solution } from '../../types';
import { StatusLabel, Typography, Tooltip, IconButton } from '@ellucian/react-design-system/core';
import { colorBrandNeutral500 } from '@ellucian/react-design-system/core/styles/tokens';
import { Link } from 'react-router-dom';
import { solutionLogoOrDefault } from '../../utils';
import { sanitizeContent } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { trackSolutionClick } from '../../utils/analytics';
const Award = require('@ellucian/ds-icons/lib').Award;
const BadgeDollar = require('@ellucian/ds-icons/lib').BadgeDollar;
const Employment = require('@ellucian/ds-icons/lib').Employment;
const HandsHelping = require('@ellucian/ds-icons/lib').HandsHelping;


/**
 * Props for the SolutionCard component.
 * @property solution - The solution object to display
 * @property selectedAuthors - (Optional) Currently selected authors
 * @property selectedTypes - (Optional) Currently selected types
 * @property selectedIntegratedSolutions - (Optional) Currently selected integrated solutions
 */
interface Props {
  solution: Solution;
  selectedAuthors?: string[];
  selectedTypes?: string[];
  selectedIntegratedSolutions?: string[];
}


/**
 * SolutionCard component
 * Displays a solution card with logo, title, author, description, tags, and entitlement status.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element}
 */
const SolutionCard: React.FC<Props> = ({ solution, selectedAuthors = [], selectedTypes = [], selectedIntegratedSolutions = [] }) => {
  // Use centralized auth hook for login state
  const { isLoggedIn } = useAuth();

  // --- Main render: solution card as a link ---
  return (
    <Link
      to={`/details/${solution.id || solution.storefrontIntegrationId}`}
      onClick={() => trackSolutionClick(solution, 'solution_grid')}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 24,
        borderRadius: 6,
        border: '1px solid var(--Neutral-neutral-300, #D9D9D9)',
        background: '#FFF',
        flex: '1 0 0',
        minHeight: 389,
        minWidth: 0,
        maxWidth: '100%', // fill grid column
        boxSizing: 'border-box',
        position: 'relative',
        fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
        fontWeight: 400,
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s',
        paddingBottom: 48,
      }}
      tabIndex={0}
      role="link"
      aria-label={`View details for ${solution.name}`}
    >
      {/* Top row: logo (left), entitlement status (right) */}
      <div style={{ display: 'flex', width: '100%', alignItems: 'flex-start', marginBottom: 16 }}>
        {/* Logo */}
        <div
          style={{
            width: 60,
            height: 60,
            aspectRatio: '1/1',
            borderRadius: 6,
            background: '#FFFFFF',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #E9E9E9',
          }}
        >
          <img
            src={solutionLogoOrDefault(solution.solutionImage, solution.category)}
            alt={`${solution.name} logo`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              borderRadius: 6,
            }}
          />
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {/* Entitlement status label (right-aligned) */}
          {isLoggedIn && solution.isEntitled && (
            <StatusLabel
              id={`solutionCard_Entitled`}
              text="Entitled"
              type="success"
              icon={<Award />}
            />
          )}
          {isLoggedIn && solution.productizedIntegrationType === 'Ellucian Premium' && (
            <StatusLabel
              id={`solutionCard_AdditionalPurchase`}
              text="Additional Purchase"
              type="draft"
              icon={<BadgeDollar />}
            />
          )}
        </div>
      </div>

      {/* Title */}
      <Typography
        variant="h3"
        style={{
          fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
          fontWeight: 700,
          fontSize: 18,
          fontStyle: 'normal',
          lineHeight: '21px',
          letterSpacing: '0.2px',
          color: 'var(--Neutral-neutral-600, #151618)',
          alignSelf: 'stretch',
          width: '100%',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          marginBottom: 0,
        }}
      >
        {solution.name}
      </Typography>

      {/* Author */}
      <Typography
        variant="body3"
        style={{
          fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
          color: 'var(--Neutral-neutral-500, #5B5E65)',
          marginBottom: 8, // add 8px gap below author
        }}
      >
        {solution.builtBy || solution.accountName || 'Unknown Author'}
      </Typography>

      {/* Description (truncated to 4 lines) */}
      <Typography
        variant="body2"
        style={{
          fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
          color: 'var(--Neutral-neutral-600, #151618)',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 4, // limit to 4 lines
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          alignSelf: 'stretch',
          margin: '0 0 16px 0',
        }}
        dangerouslySetInnerHTML={sanitizeContent(solution.description || '', true)} 
      />

      {/* Tags - always at bottom, show at least 2 if available */} 
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          alignItems: 'center',
          alignContent: 'center',
          width: '100%',
          minHeight: 38, // ensures space for at least one row of tags
        }}
      >
        {/* Show up to 2 tags, or all if less than 2 */}
        {(solution.tags ?? []).slice(0, 2).map((tag, i) => (
          <div
            key={i}
            className="solution-tag"
            style={{
              display: 'inline-flex',
              // use CSS variable so tablet media queries can increase padding
              padding: 'var(--chip-padding, 7px 14px)',
              alignItems: 'center',
              borderRadius: '20px',
              border: '1px solid var(--Neutral-neutral-400, #C3C5C9)',
              background: '#FFF',
              boxSizing: 'border-box',
              // allow chip label to wrap on narrow screens but only at word boundaries
              whiteSpace: 'normal',
              overflowWrap: 'normal',
              wordBreak: 'normal',
              hyphens: 'none',
              // allow chip to shrink but not force the card width
              flex: '0 1 auto',
              maxWidth: '100%',
              alignSelf: 'flex-start',
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                color: colorBrandNeutral500,
                fontSize: 'var(--chip-font-size, 14px)',
                lineHeight: '18px',
              }}
            >
              {tag}
            </Typography>
          </div>
        ))}
        {/* Show "+N" if more than 2 tags */}
        {(solution.tags ?? []).length > 2 && (
          <span
            className="solution-tag"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '20px',
              border: '1px solid var(--Neutral-neutral-400, #C3C5C9)',
              padding: 'calc(var(--chip-padding, 7px 14px))',
              background: '#FFF',
              color: 'var(--Neutral-neutral-500, #5B5E65)',
              marginLeft: 8,
              fontSize: 'var(--chip-font-size, 14px)'
            }}
          >
            +{solution.tags.length - 2}
          </span>
        )}
      </div>
      
      {/* Service Icons with Tooltip - shown at bottom based on additionalPurchaseTypes */}
      {isLoggedIn && solution.additionalPurchaseTypes && solution.additionalPurchaseTypes.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '0 24px',
          height: '40px',
          backgroundColor: 'var(--Neutral-neutral-200, #F8F8F8)',
          borderTop: '1px solid var(--Neutral-neutral-300, #D9D9D9)',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {solution.additionalPurchaseTypes.includes('Professional Services Recommended') && (
            <Tooltip title="Professional services required">
              <IconButton color="gray" id="solutionCard_ProfessionalServices" aria-label="Professional Services" size="small" sx={{ marginRight: '-8px' }}>
                <Employment />
              </IconButton>
            </Tooltip>
          )}
          {solution.additionalPurchaseTypes.includes('Partner Services Recommended') && (
            <Tooltip title="Partner services required">
              <IconButton color="gray" id="solutionCard_PartnerServices" aria-label="Partner Services" size="small">
                <HandsHelping />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
    </Link>
  );
};

export default SolutionCard;
